import React, { useState } from 'react';
import {
  isArray,
  isObject,
  normalizeTimeValue,
  IGNORED_FIELDS,
} from '../helpers';
import { getProgramTitle } from '../labelGetters';
import {
  TranslationDisplay,
  TranslationDiffDisplay,
  ModifiedBadge,
  FieldLabel,
} from '../displays';
import ArrayDiff from './ArrayDiff';
import PrimitiveDiff from './PrimitiveDiff';
import TextDiff from './TextDiff';

// Check if value is a position object { id, name }
const isPositionObject = (val) => {
  return isObject(val) && 'id' in val && 'name' in val;
};

// Get position display name from object or primitive
const getPositionName = (val) => {
  if (isPositionObject(val)) return val.name;
  return val ? String(val) : '';
};

export default function ProgramDiff({
  beforeProgram,
  afterProgram,
  labels,
  attachmentLabels,
}) {
  const [translationsExpanded, setTranslationsExpanded] = useState(false);
  const title = getProgramTitle(afterProgram || beforeProgram, labels);

  // Find changed fields (excluding translations which we handle specially)
  const allKeys = [
    ...new Set([
      ...Object.keys(beforeProgram || {}),
      ...Object.keys(afterProgram || {}),
    ]),
  ];
  const simpleChanges = allKeys
    .filter((key) => key !== 'translations' && key !== 'id')
    .filter((key) => !IGNORED_FIELDS.includes(key))
    .filter((key) => {
      const beforeVal = beforeProgram?.[key];
      const afterVal = afterProgram?.[key];

      // For time fields, normalize before comparing
      if (key === 'arrivalTime' || key === 'departureTime') {
        const normalizedBefore = normalizeTimeValue(beforeVal);
        const normalizedAfter = normalizeTimeValue(afterVal);
        return normalizedBefore !== normalizedAfter;
      }

      return JSON.stringify(beforeVal) !== JSON.stringify(afterVal);
    });

  // Handle translations separately
  const beforeTranslations = beforeProgram?.translations || [];
  const afterTranslations = afterProgram?.translations || [];

  // Build translation lookup by locale
  const beforeTranslationsByLocale = new Map();
  beforeTranslations.forEach((t) => {
    if (t.locale) beforeTranslationsByLocale.set(t.locale, t);
  });

  const afterTranslationsByLocale = new Map();
  afterTranslations.forEach((t) => {
    if (t.locale) afterTranslationsByLocale.set(t.locale, t);
  });

  // Find translation changes
  const allLocales = new Set([
    ...beforeTranslationsByLocale.keys(),
    ...afterTranslationsByLocale.keys(),
  ]);

  const translationChanges = [];
  allLocales.forEach((locale) => {
    const before = beforeTranslationsByLocale.get(locale);
    const after = afterTranslationsByLocale.get(locale);
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      translationChanges.push({ locale, before, after });
    }
  });

  return (
    <div className="p-2 bg-gray-50 rounded border border-amber-200">
      <div className="flex items-center gap-2 mb-2">
        <i className="fa-regular fa-calendar-day text-indigo-500" />
        <span className="text-sm font-medium text-gray-700">{title}</span>
        <ModifiedBadge />
      </div>

      {/* Show simple field changes */}
      {simpleChanges.length > 0 && (
        <div className="space-y-2 mb-2">
          {simpleChanges.map((key) => {
            const beforeVal = beforeProgram?.[key];
            const afterVal = afterProgram?.[key];

            // Handle position objects specially
            if (
              key === 'startPosition' ||
              key === 'endPosition' ||
              isPositionObject(beforeVal) ||
              isPositionObject(afterVal)
            ) {
              const beforeName = getPositionName(beforeVal);
              const afterName = getPositionName(afterVal);
              return (
                <div key={key}>
                  <FieldLabel name={key} />
                  <TextDiff beforeValue={beforeName} afterValue={afterName} />
                </div>
              );
            }

            return (
              <div key={key}>
                <FieldLabel name={key} />
                {isArray(beforeVal) || isArray(afterVal) ? (
                  <ArrayDiff
                    beforeValue={beforeVal}
                    afterValue={afterVal}
                    depth={1}
                    attachmentLabels={attachmentLabels}
                    labels={labels}
                    fieldName={key}
                  />
                ) : (
                  <PrimitiveDiff
                    beforeValue={beforeVal}
                    afterValue={afterVal}
                    fieldName={key}
                    labels={labels}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Show translation changes with clear language context */}
      {translationChanges.length > 0 && (
        <div className="border-t border-gray-200 pt-2 mt-2">
          <button
            type="button"
            className="py-1 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 w-full border-none"
            onClick={() => setTranslationsExpanded(!translationsExpanded)}
          >
            <i
              className={`fa-regular fa-chevron-${translationsExpanded ? 'down' : 'right'} text-gray-400 w-3`}
            />
            <i className="fa-regular fa-language text-gray-400" />
            <span>Translations ({translationChanges.length})</span>
            <ModifiedBadge />
          </button>
          {translationsExpanded && (
            <div className="mt-2 space-y-2">
              {translationChanges.map(({ locale, before, after }) => {
                // Added translation
                if (!before && after) {
                  return (
                    <div key={locale}>
                      <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-green-100 text-green-700">
                        +
                      </span>
                      <TranslationDisplay translation={after} isNew />
                    </div>
                  );
                }
                // Removed translation
                if (before && !after) {
                  return (
                    <div key={locale}>
                      <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-red-100 text-red-700">
                        -
                      </span>
                      <div className="line-through text-gray-400">
                        <TranslationDisplay translation={before} />
                      </div>
                    </div>
                  );
                }
                // Modified translation
                return (
                  <TranslationDiffDisplay
                    key={locale}
                    beforeTranslation={before}
                    afterTranslation={after}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
